import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { modifier } from 'ember-modifier';
import { htmlSafe } from '@ember/template';
import { guidFor } from '@ember/object/internals';
import { concat } from '@ember/helper';
import { t } from 'ember-intl';
import { PhCaretDown } from 'ember-phosphor-icons';
import FormField from './form-field';
import type { FieldValue } from './form';
import {
  computeDropdownPosition,
  installEscapeHandler,
  installOutsideClickHandler,
  installScrollHandler,
} from '../utils/floating';
import type IntlService from 'ember-intl/services/intl';

// ---------------------------------------------------------------------------
// PhoneInput – national-format entry, E.164 output
// ---------------------------------------------------------------------------

export type PhoneInputSize = 'sm' | 'md' | 'lg';

/** The supported markets. Deliberately closed: without numbering-plan metadata the
 * component cannot honestly validate or format anywhere else. */
export type PhoneCountryCode = 'US' | 'CA' | 'MX';

/** One entry in the country selector. */
export interface PhoneCountry {
  iso: PhoneCountryCode;
  dialCode: string;
  label: string;
}

/** What `@onValidChange` reports alongside the E.164 value. */
export interface PhoneMeta {
  /** The SELECTED country, not a detected one. `+1` is shared between the US and
   * Canada and nothing here can tell them apart by area code, so reporting a
   * detection would be a guess dressed as a fact. */
  country?: PhoneCountryCode;
}

interface ParsedPhone {
  country: PhoneCountryCode;
  national: string;
  e164: string;
}

const COUNTRY_CONFIG = {
  US: { dialCode: '1' },
  CA: { dialCode: '1' },
  MX: { dialCode: '52' },
} as const satisfies Record<PhoneCountryCode, { dialCode: string }>;

function countryForInternationalInput(
  digits: string,
  fallback: PhoneCountryCode
): PhoneCountryCode | undefined {
  if (digits.startsWith(COUNTRY_CONFIG.MX.dialCode)) return 'MX';
  if (digits.startsWith(COUNTRY_CONFIG.US.dialCode)) return fallback === 'CA' ? 'CA' : 'US';

  return undefined;
}

function nationalDigits(value: string, fallback: PhoneCountryCode) {
  const raw = value.trim();
  const digits = raw.replace(/\D/g, '');

  if (!digits) return { country: fallback, national: '' };

  if (raw.startsWith('+')) {
    const country = countryForInternationalInput(digits, fallback);
    if (!country) return undefined;

    return { country, national: digits.slice(COUNTRY_CONFIG[country].dialCode.length) };
  }

  const national =
    fallback !== 'MX' && digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  return { country: fallback, national };
}

/** NANP structure, which needs no metadata: an area code and an exchange both start
 * 2-9, and an N11 area code is a service code rather than a subscriber. Without this
 * the field goes green on numbers the server's validator rejects, and enrollment then
 * discards them — the member is told nothing at the one moment they could correct it. */
function isNanpShaped(national: string): boolean {
  const area = national.slice(0, 3);

  return /^[2-9]/.test(area) && /^[2-9]/.test(national.slice(3, 6)) && !/^\d11$/.test(area);
}

/**
 * Resolves a typed or stored value to E.164 under `fallback`, or undefined when it
 * cannot be dialled. Exported so a surface that submits without blurring — Enter from
 * inside the field — can resolve the value itself rather than depend on blur timing.
 */
export function parsePhoneNumber(
  value: string,
  fallback: PhoneCountryCode
): ParsedPhone | undefined {
  const parsed = nationalDigits(value, fallback);
  if (!parsed || parsed.national.length !== 10) return undefined;
  if (parsed.country !== 'MX' && !isNanpShaped(parsed.national)) return undefined;

  return { ...parsed, e164: `+${COUNTRY_CONFIG[parsed.country].dialCode}${parsed.national}` };
}

/** Grouping only where it is truthful: NANP is (AAA) EEE-LLLL everywhere, while
 * Mexican local-area-code lengths vary, so those digits are left ungrouped rather
 * than forced into a shape that is wrong for most of the country. */
function formatNational(value: string, fallback: PhoneCountryCode): string {
  const raw = value.trim();
  const parsed = nationalDigits(raw, fallback);
  if (!parsed) return value;

  const { country, national } = parsed;
  // A pasted E.164 stays intact until it holds a complete supported number.
  if (raw.startsWith('+') && national.length < 10) return value;
  if (country === 'MX') return national;

  if (national.length <= 3) return national;
  if (national.length <= 6) return `(${national.slice(0, 3)}) ${national.slice(3)}`;

  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6, 10)}`;
}

interface PhoneInputSignature {
  Args: {
    /** Emitted as `data-test-hook`; see `Input` for why this is not the DOM id. */
    testHook?: string;
    /** The current value, displayed in national format when it resolves to a valid
     * number of the selected region. Accepts either an E.164 string (what a server
     * stores) or the formatted national string `@onChange` reports — bind it to
     * whichever the surface holds. Empty is valid. */
    value?: FieldValue;
    /** Fires on every keystroke with the formatted national string, so a submit
     * before blur never has to reach for the DOM. */
    onChange?: (national: string) => void;
    /** Forwarded on blur so the `<Form>` orchestrator can mark the field touched. */
    onBlur?: () => void;
    /** Fires when the member picks a region, so a caller resolving the value itself
     * uses the selection rather than assuming the default. */
    onCountryChange?: (country: PhoneCountryCode) => void;
    /** Fires on blur with E.164, or null when the number cannot be dialled.
     * Blur rather than keystroke: a half-typed number is not an error yet. */
    onValidChange?: (e164: string | null, meta?: PhoneMeta) => void;
    /** Region used to interpret a number typed without a `+` prefix @default "US" */
    defaultCountry?: PhoneCountryCode;
    /** Selector contents @default ["US", "CA", "MX"] */
    countries?: PhoneCountryCode[];
    /** Caller-supplied errors; these replace the built-in invalid message. */
    errors?: string[];
    hint?: string;
    label?: string;
    /** Renders the muted "Optional" qualifier inside the label row. Qualifies
     * `@label`, so it has no effect without one — a surface that renders its own
     * heading instead should carry the wording there. */
    isOptional?: boolean;
    isRequired?: boolean;
    isDisabled?: boolean;
    /** @default "md" — every size still clears a 44px tap target. */
    size?: PhoneInputSize;
    placeholder?: string;
    name?: string;
  };
  Element: HTMLDivElement;
}

const DEFAULT_COUNTRIES: PhoneCountryCode[] = ['US', 'CA', 'MX'];
/** The region a value is read under when neither the caller nor the member names one. */
export const DEFAULT_COUNTRY: PhoneCountryCode = 'US';

/**
 * A phone field that displays national format and reports E.164.
 *
 * Supports the US, Canada and Mexico, and nothing else. That is a deliberate
 * boundary rather than a gap: with no numbering-plan metadata the component cannot
 * honestly validate or format elsewhere, and the server is the authority on whether
 * a number is dialable. What is checked here is structure — ten national digits, and
 * for the NANP an area code and exchange that could actually exist.
 *
 * `+1` cannot be split between the US and Canada without metadata, so
 * `@onValidChange` reports the SELECTED country rather than pretending to detect one.
 *
 * @example
 * ```gts
 * <PhoneInput
 *   @label="Mobile phone"
 *   @isOptional={{true}}
 *   @value={{this.phone}}
 *   @onChange={{this.setPhone}}
 *   @onValidChange={{this.setE164}}
 * />
 * ```
 */
export default class PhoneInput extends Component<PhoneInputSignature> {
  @service declare intl: IntlService;

  private fieldId = `tio-phone-input-${guidFor(this)}`;
  private triggerId = `${this.fieldId}-country`;

  @tracked private selectedCountry?: PhoneCountryCode;
  @tracked private touched = false;
  @tracked private isOpen = false;
  @tracked private triggerRect?: DOMRect;
  @tracked private activeIndex = 0;

  private triggerElement?: HTMLElement;
  private optionElements = new Map<number, HTMLElement>();

  // ---- Derived state --------------------------------------------------------

  private get size(): PhoneInputSize {
    return this.args.size ?? 'md';
  }

  private get country(): PhoneCountryCode {
    if (this.selectedCountry) return this.selectedCountry;

    const fallback = this.args.defaultCountry ?? DEFAULT_COUNTRY;
    const value = this.valueAsString.trim();

    // A stored E.164 still selects its own country, within the supported set.
    return value.startsWith('+')
      ? (countryForInternationalInput(value.replace(/\D/g, ''), fallback) ?? fallback)
      : fallback;
  }

  private get countries(): PhoneCountry[] {
    const isos = this.args.countries ?? DEFAULT_COUNTRIES;

    return isos.map((iso) => ({ iso, dialCode: this.dialCode(iso), label: this.regionName(iso) }));
  }

  private dialCode(iso: PhoneCountryCode): string {
    return `+${COUNTRY_CONFIG[iso].dialCode}`;
  }

  /** Falls back to the resolved country rather than the first entry, so a caller who
   * narrows `@countries` past the value's own country does not get a mislabelled
   * selector. */
  private get selected(): PhoneCountry {
    const listed = this.countries.find((c) => c.iso === this.country);
    if (listed) return listed;

    return {
      iso: this.country,
      dialCode: this.dialCode(this.country),
      label: this.regionName(this.country),
    };
  }

  private get valueAsString(): string {
    return this.args.value == null ? '' : String(this.args.value);
  }

  /** Derived purely from `@value`, like `Input` — the component keeps no second copy
   * to fall out of step with a form reset or a server-normalised value. */
  private get displayValue(): string {
    return this.format(this.valueAsString);
  }

  private get isEmpty(): boolean {
    return this.displayValue.trim().length === 0;
  }

  /** Empty is valid — the field is optional on every surface that uses it. */
  private get isInvalid(): boolean {
    if (!this.touched || this.isEmpty) return false;

    return !parsePhoneNumber(this.displayValue, this.country);
  }

  private get errors(): string[] | undefined {
    if (this.args.errors?.length) return this.args.errors;

    return this.isInvalid ? [this.intl.t('common.phone_input.invalid')] : undefined;
  }

  private get inputClasses(): string {
    return `app-phone-field app-input--${this.size}`;
  }

  /** The error state lives on the row, not the inner input: the shared
   * .app-input--has-error rule carries its own focus ring, which would render a
   * second square-cornered ring inside the row's rounded one. */
  private get rowClasses(): string {
    return this.errors?.length ? 'app-phone-row app-phone-row--has-error' : 'app-phone-row';
  }

  private get dropdownStyle() {
    if (!this.triggerRect) return htmlSafe('display:none;');

    const pos = computeDropdownPosition(this.triggerRect, { maxHeight: 260, offset: 4 });

    return htmlSafe(
      `position: fixed; top: ${pos.top}px; left: ${pos.left}px; min-width: ${Math.max(pos.width, 220)}px;` +
        ` max-height: 260px; transform-origin: ${pos.transformOrigin};` +
        ` transform: ${pos.openAbove ? 'translateY(-100%)' : 'none'}; z-index: 90;`
    );
  }

  // ---- Phone number plumbing ------------------------------------------------

  private format(value: string): string {
    return formatNational(value, this.country);
  }

  private regionName(iso: PhoneCountryCode): string {
    try {
      const locale = this.intl.primaryLocale ?? 'en';

      return new Intl.DisplayNames([locale], { type: 'region' }).of(iso) ?? iso;
    } catch {
      return iso;
    }
  }

  // ---- Event handlers -------------------------------------------------------

  handleInput = (event: Event) => {
    this.args.onChange?.(this.format((event.target as HTMLInputElement).value));
  };

  handleBlur = () => {
    this.touched = true;
    this.args.onBlur?.();

    if (this.isEmpty) {
      this.args.onValidChange?.(null);
      return;
    }

    const phone = parsePhoneNumber(this.displayValue, this.country);

    this.args.onValidChange?.(phone?.e164 ?? null, phone ? { country: phone.country } : undefined);
  };

  // ---- Country selector -----------------------------------------------------

  toggle = () => {
    if (this.args.isDisabled) return;

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  private open() {
    // Measured here rather than in a modifier: the rect has to be sampled at the
    // moment of opening, and a modifier runs on insert — by which time the trigger
    // may have moved. Select measures at the same point for the same reason.
    this.triggerRect = this.triggerElement?.getBoundingClientRect();
    this.activeIndex = Math.max(
      this.countries.findIndex((c) => c.iso === this.country),
      0
    );
    this.isOpen = true;
  }

  private close(refocus = true) {
    this.isOpen = false;
    if (refocus) this.triggerElement?.focus();
  }

  selectCountry = (iso: PhoneCountryCode) => {
    this.selectedCountry = iso;
    this.args.onCountryChange?.(iso);
    this.close();

    // The same digits mean a different number under a different region, so both the
    // display and the reported validity have to be recomputed.
    if (this.valueAsString) this.args.onChange?.(this.format(this.valueAsString));
    if (this.touched) this.handleBlur();
  };

  handleTriggerKeydown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isOpen) this.open();
    }
  };

  handleListKeydown = (event: KeyboardEvent) => {
    const last = this.countries.length - 1;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.focusOption(Math.min(this.activeIndex + 1, last));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusOption(Math.max(this.activeIndex - 1, 0));
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.focusOption(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.focusOption(last);
    } else if (event.key === 'Tab') {
      this.close(false);
    }
  };

  private focusOption(index: number) {
    this.activeIndex = index;
    this.optionElements.get(index)?.focus();
  }

  registerTrigger = modifier((element: HTMLElement) => {
    this.triggerElement = element;

    return () => {
      this.triggerElement = undefined;
    };
  });

  registerOption = modifier((element: HTMLElement, [index]: [number]) => {
    this.optionElements.set(index, element);
    if (index === this.activeIndex) element.focus();

    return () => this.optionElements.delete(index);
  });

  registerListbox = modifier((element: HTMLElement) => {
    const cleanupOutside = installOutsideClickHandler([this.triggerElement ?? null, element], () =>
      this.close(false)
    );
    const cleanupEscape = installEscapeHandler(() => this.close(), this.triggerElement);
    // position:fixed, so a scrolling modal would otherwise leave the list floating away
    // from its trigger. Matches Select's treatment.
    const cleanupScroll = installScrollHandler({
      floatingEl: element,
      mode: 'close',
      onClose: () => this.close(false),
      triggerEl: this.triggerElement,
      debounceMs: 150,
    });

    return () => {
      cleanupOutside();
      cleanupEscape();
      cleanupScroll();
    };
  });

  private get portalDestination(): Element {
    return document.body;
  }

  <template>
    <FormField
      @label={{@label}}
      @hint={{@hint}}
      @errors={{this.errors}}
      @isRequired={{@isRequired}}
      @isDisabled={{@isDisabled}}
      @size={{this.size}}
      @fieldId={{this.fieldId}}
      data-test-phone-input-root
      data-test-hook={{@testHook}}
      ...attributes
    >
      {{! Inside the label row, so it reads as a qualifier and not as a second line. }}
      <:labelSuffix>
        {{#if @isOptional}}
          <span data-test-phone-input-optional class="tio-text-xs-medium app-phone-optional">
            {{t "common.phone_input.optional"}}
          </span>
        {{/if}}
      </:labelSuffix>

      <:default as |field|>
        <div class={{this.rowClasses}}>
          <button
            data-test-phone-input-country
            id={{this.triggerId}}
            type="button"
            class="app-phone-country"
            disabled={{@isDisabled}}
            aria-haspopup="listbox"
            aria-expanded={{if this.isOpen "true" "false"}}
            aria-label={{t
              "common.phone_input.country_selected"
              country=this.selected.label
              dialCode=this.selected.dialCode
            }}
            {{this.registerTrigger}}
            {{on "click" this.toggle}}
            {{on "keydown" this.handleTriggerKeydown}}
          >
            <span aria-hidden="true">{{this.selected.iso}}</span>
            <span class="tio-fg-muted" aria-hidden="true">{{this.selected.dialCode}}</span>
            <PhCaretDown @size={{12}} aria-hidden="true" />
          </button>

          {{! template-lint-disable require-input-label }}
          {{! The two labelling paths are mutually exclusive at runtime: aria-label is
          emitted only when @label is absent, in which case FormField renders no
          <label for> at all. The rule cannot see the condition. }}
          <input
            data-test-phone-input-field
            id={{field.fieldId}}
            type="tel"
            inputmode="tel"
            autocomplete="tel"
            class={{this.inputClasses}}
            value={{this.displayValue}}
            name={{@name}}
            placeholder={{@placeholder}}
            disabled={{@isDisabled}}
            required={{@isRequired}}
            aria-label={{unless @label (t "common.phone_input.label_fallback")}}
            aria-invalid={{if field.hasErrors "true"}}
            aria-describedby={{if @hint (concat field.fieldId "-hint")}}
            {{on "input" this.handleInput}}
            {{on "blur" this.handleBlur}}
          />
        </div>

        {{#if this.isOpen}}
          {{#in-element this.portalDestination insertBefore=null}}
            <div
              data-test-phone-input-listbox
              data-floating-trigger-id={{this.triggerId}}
              role="listbox"
              aria-labelledby={{this.triggerId}}
              class="app-popover app-popover-body"
              style={{this.dropdownStyle}}
              {{this.registerListbox}}
            >
              {{#each this.countries key="iso" as |item index|}}
                <button
                  data-test-phone-input-option={{item.iso}}
                  type="button"
                  role="option"
                  aria-selected={{if (eq item.iso this.country) "true" "false"}}
                  class="app-popover-option app-popover-option--{{this.size}}
                    {{if (eq index this.activeIndex) 'app-popover-option--highlighted'}}
                    {{if (eq item.iso this.country) 'app-popover-option--selected'}}"
                  {{this.registerOption index}}
                  {{on "click" (fn this.selectCountry item.iso)}}
                  {{on "keydown" this.handleListKeydown}}
                >
                  <span>{{item.label}}</span>
                  <span class="tio-fg-muted">{{item.dialCode}}</span>
                </button>
              {{/each}}
            </div>
          {{/in-element}}
        {{/if}}
      </:default>
    </FormField>
  </template>
}

export type { PhoneInputSignature };
